import { LitElement, html, TemplateResult, nothing, PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { editIcon, imageIcon } from "@/assets/icons";
import {
  IProfileWidgetCustomization,
  USER_ACCOUNT_SETTINGS_WIDGETS_LAYOUT,
} from "@/types";

import {
  CARD_CONTAINER_CLASS,
  CARD_HEADER_CONTAINER_CLASS,
  CARD_HEADER_TITLE_CLASS,
  CARD_INPUT_LABEL_CLASS,
} from "../classes";
import { cn, showDestructiveToast } from "@/utils";
import { updateUser } from "@/service";
import { userContext, UserContextValue } from "@/context";
import { consume } from "@lit/context";
import { PopoverContent } from "@revotech-group/revotech-ui-kit";

export interface Account {
  email: string;
  phone: string;
  avatar: string;
}

const renderOtpContent = (variant: string, length: number) => {
  const slot = (i: number) =>
    html`<rtg-input-otp-slot index=${i}></rtg-input-otp-slot>`;
  const group = (inner: unknown) =>
    html`<rtg-input-otp-group>${inner}</rtg-input-otp-group>`;
  const sep = html`<rtg-input-otp-separator></rtg-input-otp-separator>`;
  const range = (start: number, end: number) =>
    Array.from({ length: end - start }, (_, i) => slot(start + i));

  if (variant === "simple") {
    const half = Math.ceil(length / 2);
    return html`${group(range(0, half))} ${sep} ${group(range(half, length))}`;
  }
  if (variant === "with_separator") {
    const third = Math.ceil(length / 3);
    return html`
      ${group(range(0, third))} ${sep} ${group(range(third, third * 2))} ${sep}
      ${group(range(third * 2, length))}
    `;
  }
  if (variant === "with_spacing") {
    return html`
      ${Array.from(
        { length },
        (_, i) => html`<rtg-input-otp-group>${slot(i)}</rtg-input-otp-group>`,
      )}
    `;
  }
  return html`${group(range(0, length))}`;
};

@customElement("sb-account-info")
export class SbAccountInfo extends LitElement {
  @property({ type: Object }) account!: Account;
  @property({ type: Boolean }) showAvatarMenu = false;
  @property({ attribute: false }) toggleAvatarMenu!: (e: Event) => void;
  @property({ attribute: false }) onAvatarChange!: () => void;
  @property({ attribute: false }) onAvatarRemove!: () => void;
  @property({ type: Boolean }) showIcons = true;
  @property({ type: Object }) content: IProfileWidgetCustomization | null =
    null;
  @property({ type: String }) otpVariant:
    | "simple"
    | "with_spacing"
    | "with_separator"
    | "digits_only" = "simple";
  @property({ type: Number }) otpLength = 6;
  @property({ type: String }) layout: USER_ACCOUNT_SETTINGS_WIDGETS_LAYOUT =
    USER_ACCOUNT_SETTINGS_WIDGETS_LAYOUT.ROW;

  @state() private isEditingEmail = false;
  @state() private isVerifyingEmail = false;
  @state() private isEditingPhone = false;
  @state() private isVerifyingPhone = false;
  @state() private isUploadingImage = false;
  @state() private isEditingAvatarMobile = false;
  @state() private isMobile = false;
  @state() private _isAvatarEditorOpen = false;
  private _popoverObserver: MutationObserver | null = null;
  private _intentionalClose = false;

  // Holds the in-progress URL while the user is editing.
  // The real account.avatar is NOT touched until Save succeeds.
  @state() private avatarDraft = "";
  private _committedAvatar = "";

  @state()
  private formData: Account = { avatar: "", email: "", phone: "" };

  @consume({ context: userContext, subscribe: true })
  private userContextValue!: UserContextValue;

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  private _mobileQuery!: MediaQueryList;
  private _onMobileChange = (e: MediaQueryListEvent) => {
    this.isMobile = e.matches;
    if (!e.matches) this.isEditingAvatarMobile = false;
  };

  firstUpdated() {
    this._setupPopoverObserver();
  }

  private _setupPopoverObserver() {
    const popoverContent = this.querySelector(
      "rtg-popover-content",
    ) as HTMLElement;
    if (!popoverContent) return;

    // Create a MutationObserver to watch for the inner div being added
    const childObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "childList") {
          const innerDiv = popoverContent.firstElementChild as HTMLElement;
          if (innerDiv && innerDiv.hasAttribute("data-state")) {
            // Found the inner div – now attach the attribute observer
            this._attachAttributeObserver(innerDiv);
            childObserver.disconnect(); // no longer needed
            break;
          }
        }
      }
    });
    childObserver.observe(popoverContent, { childList: true, subtree: false });

    // Also check if the inner div already exists (in case popover is already opened)
    const existingInner = popoverContent.firstElementChild as HTMLElement;
    if (existingInner && existingInner.hasAttribute("data-state")) {
      this._attachAttributeObserver(existingInner);
      childObserver.disconnect();
    }
  }

  private _attachAttributeObserver(innerDiv: HTMLElement) {
    if (this._popoverObserver) this._popoverObserver.disconnect();
    this._popoverObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "data-state"
        ) {
          const newState = innerDiv.getAttribute("data-state");
          // console.log("data-state changed to", newState);
          if (newState === "open") {
            this.openAvatarEditor();
          } else if (newState === "closed") {
            if (this._intentionalClose) {
              this._intentionalClose = false;
            } else {
              // Revert on outside click / escape
              this.account = { ...this.account, avatar: this._committedAvatar };
              this.avatarDraft = this._committedAvatar;
              this._isAvatarEditorOpen = false;
            }
          }
        }
      }
    });
    this._popoverObserver.observe(innerDiv, { attributes: true });
    // console.log("Attribute observer attached to inner div");
  }

  protected updated(_changedProperties: PropertyValues): void {
    // Only sync from the external account prop when we are NOT editing
    if (
      _changedProperties.has("account") &&
      this.account.avatar &&
      !this._isAvatarEditorOpen
    ) {
      this.avatarDraft = this.account.avatar;
      this._committedAvatar = this.account.avatar;
    }
  }

  connectedCallback() {
    super.connectedCallback();
    //TO DO: IF USER WAS ABLE TO CHANGE BREAKPOINTS IN THE FUTURE THIS SHOULDNT BE HARD CODED
    this._mobileQuery = window.matchMedia("(max-width: 767px)");
    this.isMobile = this._mobileQuery.matches;
    this._mobileQuery.addEventListener("change", this._onMobileChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._mobileQuery.removeEventListener("change", this._onMobileChange);
    if (this._popoverObserver) {
      this._popoverObserver.disconnect();
      this._popoverObserver = null;
    }
  }

  // ─── Avatar draft helpers ─────────────────────────────────────────────────

  /** Call this when the popover/inline form opens. */
  private openAvatarEditor() {
    this._isAvatarEditorOpen = true;
    this._committedAvatar = this.account.avatar;
    this.avatarDraft = this.account.avatar;
  }
  /** Call this on cancel or popover-dismissed-without-save. */
  private resetAvatarDraft() {
    this.avatarDraft = this.account.avatar;
  }

  private handleAvatarInput = (e: Event) => {
    this.avatarDraft = (e.target as HTMLInputElement).value;
    // ← Real-time preview: keep account.avatar in sync so the
    //   single existing rtg-avatar re-renders immediately.
    //   On cancel we restore the original value.
    this.account = { ...this.account, avatar: this.avatarDraft };
  };

  private handleCancelAvatar() {
    if (this.isUploadingImage) return;

    this.account = { ...this.account, avatar: this._committedAvatar };
    this.avatarDraft = this._committedAvatar;
    this._isAvatarEditorOpen = false;

    if (this.isMobile) {
      this.isEditingAvatarMobile = false;
    } else {
      this._intentionalClose = true;
      (this.querySelector("rtg-popover-content") as PopoverContent)!.state =
        "closed";
    }
  }

  private handleSaveAvatar = async () => {
    if (this.isUploadingImage) return;
    this.isUploadingImage = true;

    try {
      const updatedUser = await updateUser(this.userContextValue.identifier!, {
        picture: this.avatarDraft,
      });

      this.account = { ...this.account, avatar: updatedUser.picture ?? "" };
      this._committedAvatar = this.account.avatar;
      this.avatarDraft = this.account.avatar;
      this._isAvatarEditorOpen = false;

      if (!this.isMobile) {
        this._intentionalClose = true;
        (this.querySelector("rtg-popover-content") as PopoverContent)!.state =
          "closed";
      } else {
        this.isEditingAvatarMobile = false;
      }
    } catch (error) {
      // ... error handling (revert, toast, etc.)
      // Then close popover (for desktop) with intentional flag
      if (!this.isMobile) {
        this._intentionalClose = true;
        (this.querySelector("rtg-popover-content") as PopoverContent)!.state =
          "closed";
      } else {
        this.isEditingAvatarMobile = false;
      }
    } finally {
      this.isUploadingImage = false;
    }
  };

  // ─── Shared rendering helpers ─────────────────────────────────────────────

  private renderDisplayRow(label: string, value: string, onEdit: () => void) {
    const isColumn =
      this.layout === USER_ACCOUNT_SETTINGS_WIDGETS_LAYOUT.COLUMN;

    return html`
      <div
        class=${cn(
          "sb-account-settings-display-row",
          isColumn
            ? ""
            : "sb-grid sb-account-settings-account-grid sb-items-center",
        )}
      >
        ${isColumn
          ? html`
              <div class="sb-account-settings-display-row-labels">
                <div part="label" class=${CARD_INPUT_LABEL_CLASS}>${label}</div>
              </div>
              <div class="sb-flex-1 sb-min-w-0">
                <rtg-typography-p>
                  ${value
                    ? value
                    : html`<p class=${CARD_INPUT_LABEL_CLASS}>Not Added</p>`}
                </rtg-typography-p>
              </div>
            `
          : html`
              <div
                class=${this.layout === USER_ACCOUNT_SETTINGS_WIDGETS_LAYOUT.ROW
                  ? "sb-flex sb-w-full sb-items-center"
                  : ""}
              >
                <div
                  part="label"
                  class=${cn(
                    CARD_INPUT_LABEL_CLASS,
                    "sb-account-settings-field-label",
                  )}
                >
                  ${label}
                </div>
                <div class="sb-min-w-0 sb-flex-1">
                  <rtg-typography-p>
                    ${value
                      ? value
                      : html`<p class=${CARD_INPUT_LABEL_CLASS}>Not Added</p>`}
                  </rtg-typography-p>
                </div>
                <div class="sb-account-settings-display-row-action"></div>
              </div>
            `}
      </div>
    `;
  }

  private renderEditForm(
    type: "email" | "phone",
    placeholder: string,
    onSend: () => void,
    onCancel: () => void,
  ) {
    const title = type === "email" ? "Edit email address" : "Edit phone number";
    const desc =
      type === "email"
        ? "Type your new email address to replace the old one."
        : "Type your new phone number to replace the old one.";

    return html`
      <rtg-card
        part="edit-form"
        class="sb-account-settings-card-form-margin-top"
      >
        <div class="sb-account-settings-form-body">
          <rtg-card-title class="sb-flex sb-items-center sb-justify-between">
            <span class="sb-account-settings-card-section-title">${title}</span>
          </rtg-card-title>
          <rtg-card-description>
            <div class="sb-account-settings-card-description">${desc}</div>
          </rtg-card-description>
          <rtg-card-content>
            <div class="sb-account-settings-form-body">
              <rtg-input
                placeholder=${placeholder}
                type=${type === "phone" ? "tel" : "text"}
              ></rtg-input>
            </div>
          </rtg-card-content>
          <rtg-separator></rtg-separator>
          <rtg-card-footer>
            <div class="sb-account-settings-card-actions">
              <rtg-button @click=${onSend}>Send Code</rtg-button>
              <rtg-button variant="ghost" @click=${onCancel}>Cancel</rtg-button>
            </div>
          </rtg-card-footer>
        </div>
      </rtg-card>
    `;
  }

  private renderVerificationForm(
    type: "email" | "phone",
    onCancel: () => void,
  ) {
    const title =
      type === "email"
        ? "Email verification required"
        : "Phone number verification required";
    const desc =
      type === "email"
        ? "Please enter the code to verify this email."
        : "Please enter the code to verify this phone number.";

    return html`
      <rtg-card part="verification-form">
        <div class="sb-account-settings-form-body">
          <rtg-card-title class="sb-flex sb-items-center sb-justify-between">
            <span class="sb-account-settings-card-section-title">${title}</span>
          </rtg-card-title>
          <rtg-card-description>
            <div class="sb-account-settings-card-description">${desc}</div>
          </rtg-card-description>
          <rtg-card-content>
            <div
              class="sb-account-settings-form-body sb-flex sb-items-center sb-flex-col"
            >
              <sb-otp
                id="otp"
                name="code"
                data-key="code"
                variant=${this.otpVariant}
                length="6"
                timeout="300"
                event="verify_code"
                resend-button-text="Resend Verification Code"
                resend-event="forgot_password_resend_code"
              ></sb-otp>
            </div>
          </rtg-card-content>
          <rtg-separator></rtg-separator>
          <rtg-card-footer>
            <div class="sb-account-settings-card-actions">
              <rtg-button>Confirm</rtg-button>
              <rtg-button variant="ghost" @click=${onCancel}>Cancel</rtg-button>
            </div>
          </rtg-card-footer>
        </div>
      </rtg-card>
    `;
  }

  private updateField<K extends keyof Account>(key: K, value: string) {
    this.formData = { ...this.formData, [key]: value };
  }

  // ─── Email / Phone sections (unchanged) ──────────────────────────────────

  private renderEmailSection() {
    if (this.isVerifyingEmail)
      return this.renderVerificationForm(
        "email",
        () => ((this.isVerifyingEmail = false), (this.isEditingEmail = true)),
      );
    if (this.isEditingEmail)
      return this.renderEditForm(
        "email",
        "Email",
        () => ((this.isVerifyingEmail = true), (this.isEditingEmail = false)),
        () => (this.isEditingEmail = false),
      );
    return this.renderDisplayRow(
      "Email",
      this.account.email,
      () => (this.isEditingEmail = true),
    );
  }

  private renderPhoneSection() {
    if (this.isVerifyingPhone)
      return this.renderVerificationForm(
        "phone",
        () => ((this.isVerifyingPhone = false), (this.isEditingPhone = true)),
      );
    if (this.isEditingPhone)
      return this.renderEditForm(
        "phone",
        "Phone Number",
        () => ((this.isVerifyingPhone = true), (this.isEditingPhone = false)),
        () => (this.isEditingPhone = false),
      );
    return this.renderDisplayRow(
      "Phone number",
      this.account.phone,
      () => (this.isEditingPhone = true),
    );
  }

  // ─── Avatar form content (shared between popover and mobile inline) ───────

  private renderAvatarFooter(): TemplateResult {
    return html`
      <rtg-card-footer>
        <div class="sb-account-settings-card-actions">
          <rtg-button
            variant="default"
            ?disabled=${this.isUploadingImage}
            @click=${this.handleSaveAvatar}
          >
            ${this.isUploadingImage
              ? html`<rtg-spinner class="rtg-icon-inline-start"></rtg-spinner
                  >Submitting`
              : "Submit"}
          </rtg-button>
          <rtg-button
            variant="ghost"
            ?disabled=${this.isUploadingImage}
            @click=${() => this.handleCancelAvatar()}
          >
            Cancel
          </rtg-button>
        </div>
      </rtg-card-footer>
    `;
  }

  /**
   * The URL input form — no extra avatar image inside here.
   * The live preview happens on the existing rtg-avatar (desktop popover
   * trigger or mobile avatar) because account.avatar is mutated on every
   * keystroke and restored on cancel.
   */
  private renderAvatarFormBody(): TemplateResult {
    return html`
      <div class="sb-flex sb-flex-col sb-gap-6">
        <div class="sb-account-settings-form-body">
          <div class=${CARD_HEADER_TITLE_CLASS}>
            <span>Avatar</span>
          </div>
          <div class="sb-flex sb-gap-2 sb-flex-col">
            <rtg-typography-p><span>Photo URL</span></rtg-typography-p>
            <rtg-input
              .value=${this.avatarDraft}
              ?disabled=${this.isUploadingImage}
              @input=${this.handleAvatarInput}
            ></rtg-input>
          </div>
        </div>

        ${this.renderAvatarFooter()}
      </div>
    `;
  }

  // ─── Desktop avatar (popover, existing behaviour) ─────────────────────────

  private renderAvatarDesktop(): TemplateResult {
    return html`
      <div class="sb-account-settings-avatar-wrapper">
        <rtg-avatar size="lg">
          ${this.account.avatar
            ? html`<rtg-avatar-image
                src=${this.account.avatar}
              ></rtg-avatar-image>`
            : html`<rtg-avatar-fallback></rtg-avatar-fallback>`}
        </rtg-avatar>
        <div class="sb-absolute sb-bottom-5 sb-right-5">
          <rtg-popover part="account-info-popover">
            <rtg-popover-trigger>
              <rtg-button
                size="icon-lg"
                part="upload-image-button"
                variant="secondary"
                >${imageIcon()}</rtg-button
              >
            </rtg-popover-trigger>

            <rtg-popover-content data-testid="popover-content">
              ${this.renderAvatarFormBody()}
            </rtg-popover-content>
          </rtg-popover>
        </div>
      </div>
    `;
  }

  // ─── Mobile avatar (large centered, click to toggle inline form) ──────────

  private renderAvatarMobile(): TemplateResult {
    return html`
      <div class="sb-flex sb-flex-col sb-items-center sb-gap-4 sb-m-2">
        <!-- Single large avatar — clicking it toggles the inline form.
             account.avatar is mutated on input for live preview and
             restored on cancel, so this single element is the preview. -->
        <div class="sb-relative">
          <rtg-avatar size="lg">
            ${this.account.avatar
              ? html`<rtg-avatar-image
                  src=${this.account.avatar}
                ></rtg-avatar-image>`
              : html`<rtg-avatar-fallback></rtg-avatar-fallback>`}
          </rtg-avatar>

          <div class="sb-absolute sb-bottom-0 sb-right-2">
            <rtg-button
              size="icon-lg"
              part="upload-image-button"
              variant="secondary"
              @click=${() => {
                if (!this.isEditingAvatarMobile) {
                  this.openAvatarEditor();
                  this.isEditingAvatarMobile = true;
                }
              }}
              >${imageIcon()}</rtg-button
            >
          </div>
        </div>

        ${this.isEditingAvatarMobile
          ? html`
              <rtg-card part="edit-form" class="sb-w-full">
                ${this.renderAvatarFormBody()}
              </rtg-card>
            `
          : nothing}
      </div>
    `;
  }

  // ─── Header ───────────────────────────────────────────────────────────────

  private renderHeader() {
    return html`
      <rtg-card-header>
        <div class=${CARD_HEADER_CONTAINER_CLASS}>
          <rtg-card-title>
            <div class=${CARD_HEADER_TITLE_CLASS}>
              ${this.content?.icon}
              <span>${this.content?.title || "Login Info"}</span>
            </div>
          </rtg-card-title>
        </div>
      </rtg-card-header>
    `;
  }

  private renderAccountDetails() {
    return html`
      <div class="sb-account-settings-account-details">
        ${this.renderEmailSection()} ${this.renderPhoneSection()}
      </div>
    `;
  }

  // ─── Root render ──────────────────────────────────────────────────────────

  render() {
    return html`
      <rtg-card>
        <div class=${CARD_CONTAINER_CLASS}>
          ${this.renderHeader()}
          <rtg-card-content>
            ${this.isMobile
              ? html`
                  <!-- Mobile: avatar (large, centered) above the fields -->
                  <div class="sb-flex sb-flex-col sb-gap-4 ">
                    ${this.renderAvatarMobile()} ${this.renderAccountDetails()}
                  </div>
                `
              : html`
                  <!-- Desktop: avatar (popover) beside the fields -->
                  <div class="sb-flex sb-flex-row sb-items-center">
                    ${this.renderAvatarDesktop()} ${this.renderAccountDetails()}
                  </div>
                `}
          </rtg-card-content>
        </div>
      </rtg-card>
    `;
  }

  createRenderRoot() {
    return this;
  }
}
